import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Box, FlexBox, FormDialog, Grid, Icon, Typography } from '@kis-technologies/kis-ui';
import { useStyles } from '../styled';

interface SalesReportOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  isSFOT: boolean;
  onApply: (payload: { date?: string | null; fromDate?: string | null; toDate?: string | null }) => void;
}

export const SalesReportOverlay = ({ isOpen, onClose, isSFOT, onApply }: SalesReportOverlayProps) => {
  const classes = useStyles({ isOpen })();

  const [date, setDate] = useState<Date | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);

    setStartDate(start);
    setEndDate(now);
  }, [isOpen]);

  const handleApply = () => {
    if (isSFOT) {
      if (!date) {
        return;
      }

      const payload = {
        date: date.toISOString(),
        fromDate: null,
        toDate: null
      };
      onApply(payload);
    } else {
      if (startDate && endDate && startDate > endDate) {
        return;
      }

      const payload = {
        fromDate: startDate ? startDate.toISOString() : null,
        toDate: endDate ? endDate.toISOString() : null,
        date: null
      };
      onApply(payload);
    }

    onClose();
  };

  const disableApply = isSFOT ? !date : !startDate || !endDate;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const DatePickerCustomInput = React.forwardRef((props1: any, ref: any) => {
    return (
      <div
        onClick={props1.onClick}
        ref={ref}
        className={classes.datePickerInput}
        style={{ padding: props1.value ? '0 40px 0 12px' : '0 12px' }}
      >
        <label>{props1.value || props1.placeholder}</label>
        <Icon name="CalendarToday" sx={{ color: '#F46D2E' }} />
      </div>
    );
  });

  return (
    <FormDialog
      open={isOpen}
      title="Sales Report"
      cancelButtonText="Cancel"
      confirmButtonText="Build Report"
      cancelButtonAction={onClose}
      confirmButtonAction={handleApply}
      confirmButtonDisabled={disableApply}
    >
      <Grid direction="column" width="100%" marginTop="10px">
        {isSFOT ? (
          <FlexBox className={classes.filtersFieldsFlexBox}>
            <Box className={classes.filtersFieldBox}>
              <Typography className={classes.filtersFieldLabel}>Select Date</Typography>
              <div className="my-datepicker-wrapper">
                <div className={classes.datePickerContainer}>
                  <DatePicker
                    selected={date}
                    onChange={(d) => setDate(d)}
                    placeholderText="MM/DD/YYYY"
                    dateFormat="MM/dd/yyyy"
                    className={classes.datePickerComponent}
                    isClearable
                    customInput={<DatePickerCustomInput />}
                    popperPlacement="bottom-start"
                    popperProps={{ strategy: 'fixed' }}
                  />
                </div>
              </div>
            </Box>
          </FlexBox>
        ) : (
          <FlexBox className={classes.filtersFieldsFlexBox}>
            <Box className={classes.filtersFieldBox}>
              <Typography className={classes.filtersFieldLabel}>Start Date and Time</Typography>
              <div className="my-datepicker-wrapper">
                <div className={classes.datePickerContainer}>
                  <DatePicker
                    showTimeSelect
                    selected={startDate}
                    onChange={(d) => setStartDate(d)}
                    placeholderText="MM/DD/YYYY hh:mm aa"
                    dateFormat="MM/dd/yyyy h:mm aa"
                    className={classes.datePickerComponent}
                    isClearable
                    customInput={<DatePickerCustomInput />}
                    popperPlacement="bottom-start"
                    calendarClassName="my-custom-calendar"
                    popperProps={{ strategy: 'fixed' }}
                  />
                </div>
              </div>
            </Box>

            <Box className={classes.filtersFieldBox}>
              <Typography className={classes.filtersFieldLabel}>End Date and Time</Typography>
              <div className={`${classes.datePickerContainer} ${classes.rightDatePickerContainer}`}>
                <DatePicker
                  showTimeSelect
                  selected={endDate}
                  onChange={(d) => setEndDate(d)}
                  placeholderText="MM/DD/YYYY hh:mm aa"
                  dateFormat="MM/dd/yyyy h:mm aa"
                  className={classes.datePickerComponent}
                  isClearable
                  popperPlacement="bottom-start"
                  popperProps={{ strategy: 'fixed' }}
                  customInput={<DatePickerCustomInput />}
                />
              </div>
            </Box>
          </FlexBox>
        )}
      </Grid>
    </FormDialog>
  );
};
